// src/components/DdosTesting/TestResults.jsx
import React, { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion'; // Import animation

// Changed prop: receives a single result object for the last test, or null/undefined
const TestResults = ({ results }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // If no results object is provided, show a placeholder or nothing
    if (!results) {
        return (
             <div className="mb-8 transform-gpu transition-medium hover:scale-[1.005]">
                 {/* ... Header ... */}
                 <h3 className="font-bold text-lg mb-5 flex items-center group">
                    <div className="w-10 h-10 rounded-lg bg-accent-blue/20 flex items-center justify-center mr-3 transform-gpu group-hover:rotate-12 transition-medium">
                        <i className="fas fa-history text-accent-blue"></i>
                    </div>
                    Test Results & History
                </h3>
                <div className="bg-dark-600 rounded-2xl p-6 hover:shadow-md transition-medium border border-dark-400 hover:border-accent-blue/30 text-center text-gray-400">
                    No test results available yet. Run a test to see results here.
                    {/* TODO: Add button to view full history (via REST?) */}
                </div>
             </div>
        );
    }

    // Determine result styling
    const resultStyle = results.success
        ? { color: 'green', text: 'Successful', icon: 'fa-check-circle' }
        : { color: 'red', text: 'Failed / Ineffective', icon: 'fa-times-circle' };

    return (
        <motion.div // Animate the appearance of the results section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="mb-8 transform-gpu transition-medium hover:scale-[1.005]"
        >
            <h3 className="font-bold text-lg mb-5 flex items-center group">
                {/* ... Header ... */}
                 <div className="w-10 h-10 rounded-lg bg-accent-blue/20 flex items-center justify-center mr-3 transform-gpu group-hover:rotate-12 transition-medium">
                     <i className="fas fa-poll text-accent-blue"></i> {/* Changed icon */}
                 </div>
                 Last Test Result
                 <span className={`ml-3 px-2 py-0.5 bg-accent-${resultStyle.color}/20 text-accent-${resultStyle.color} rounded-lg text-xs`}>
                    {resultStyle.text}
                 </span>
            </h3>

            <div className="bg-dark-600 rounded-2xl p-6 hover:shadow-lg transition-medium border border-dark-400 hover:border-accent-blue/30 relative overflow-hidden">
                {/* Background effect */}
                <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-accent-blue opacity-5 rounded-full blur-xl group-hover:opacity-10 transition-medium"></div>

                <div className="flex flex-col md:flex-row justify-between items-start mb-4">
                    <div>
                        <h4 className="font-medium text-white mb-1">Test Summary ({results.timestamp || new Date().toLocaleTimeString()})</h4>
                        <p className="text-sm text-gray-400">Type: {results.attackType || 'N/A'} | Duration: {results.duration || 'N/A'}</p>
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-sm text-accent-blue hover:text-white transition mt-2 md:mt-0 flex items-center"
                    >
                        <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} mr-1`}></i>
                        {isExpanded ? 'Show Less' : 'Show Details'}
                    </button>
                </div>

                {/* Basic Result Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4 text-sm">
                    <div className="bg-dark-500 p-3 rounded-lg border border-dark-400">
                        <span className="text-gray-400">Overall Result:</span>
                        <span className={`ml-2 font-medium text-accent-${resultStyle.color}`}>
                            <i className={`fas ${resultStyle.icon} mr-1`}></i>{resultStyle.text}
                        </span>
                    </div>
                    <div className="bg-dark-500 p-3 rounded-lg border border-dark-400">
                        <span className="text-gray-400">Target Status After:</span>
                        <span className="ml-2">{results.finalTargetStatus || 'Unknown'}</span>
                    </div>
                    <div className="bg-dark-500 p-3 rounded-lg border border-dark-400 sm:col-span-2">
                        <span className="text-gray-400">Summary Message:</span>
                        <span className="ml-2">{results.summaryMessage || 'No summary provided.'}</span>
                    </div>
                </div>

                {/* Detailed Results (Expandable) */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0, marginTop: 0 }}
                            animate={{ height: 'auto', opacity: 1, marginTop: '1rem' }} // Add margin when open
                            exit={{ height: 0, opacity: 0, marginTop: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden border-t border-dark-400 pt-4"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-xs">
                                <div>
                                    <h5 className="font-medium mb-2 text-accent-blue">Attack Metrics</h5>
                                    <div className="space-y-1">
                                        <p><span className="text-gray-400 w-28 inline-block">Max Packets/s:</span> {formatNumber(results.maxPacketsPerSec)}</p>
                                        <p><span className="text-gray-400 w-28 inline-block">Avg Bandwidth:</span> {results.avgBandwidth || 'N/A'}</p>
                                        <p><span className="text-gray-400 w-28 inline-block">Total Data Sent:</span> {results.totalDataSent || 'N/A'}</p>
                                    </div>
                                </div>
                                <div>
                                    <h5 className="font-medium mb-2 text-accent-blue">Server Response</h5>
                                    <div className="space-y-1">
                                        <p><span className="text-gray-400 w-28 inline-block">Highest Ping:</span> {results.maxPing ? `${results.maxPing} ms` : 'N/A'}</p>
                                        <p><span className="text-gray-400 w-28 inline-block">Timeouts:</span> {results.timeoutCount ?? 'N/A'}</p>
                                        <p><span className="text-gray-400 w-28 inline-block">Lowest Players:</span> {formatNumber(results.minPlayersOnline)}</p>
                                    </div>
                                </div>
                                {results.vulnerabilitiesFound && results.vulnerabilitiesFound.length > 0 && (
                                    <div className="md:col-span-2 mt-2">
                                        <h5 className="font-medium mb-1 text-accent-orange">Vulnerabilities Found:</h5>
                                        <ul className="list-disc list-inside space-y-1 text-gray-300">
                                            {results.vulnerabilitiesFound.map((vuln, i) => (
                                                <li key={i}>{vuln}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                                <button className="px-3 py-1 bg-dark-500 hover:bg-dark-400 rounded text-xs transition-medium">
                                    <i className="fas fa-file-export mr-1"></i> Export Details
                                </button>
                                {/* <button className="px-3 py-1 bg-accent-blue/20 hover:bg-accent-blue/30 text-accent-blue rounded text-xs transition-medium">
                                    <i className="fas fa-history mr-1"></i> View History
                                </button> */}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

// Helper function (duplicate from monitoring, consider moving to utils)
const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
    return num.toString();
};

export default TestResults;